import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { endpointTargetIdentity, memberIdentityOf } from '../../memberIdentity';
import { genotypesDiffer } from './freshestGenotypes';
import { notYetIn } from '../useRowRecordSync';
import { pushVariantsToGenotype } from './pushVariantToGenotype';
import { MAX_AUTOSAVE_HOLD_MS } from 'src/components/pedigree-chart/pedigree-tool/autosaveController';

// VARIANTS PUSHED FROM A TEST RECORD MUST REACH THE MEMBER'S ROW.
//
// Saving a Test pushes its variants into the person's genotypes through their
// own endpoint, POST /genotypes/:user_id/:familyMemberId/variants/push. The
// pedigree autosave, POST /pedigree-config, $sets every non-proband member's
// WHOLE row from memberRecords, genotypes included. So a push that does not
// also land on the row is undone by the next unrelated edit in the family: the
// autosave writes the row's old genotypes back and the pushed variants are gone
// from the record, silently.
//
// The row cannot be updated before the request, as a person tag can: the
// server dedupes the variants by HGVS triple, mints their ids and enriches each
// one from ClinVar, so what it stores is not what was sent. It is read back
// instead, with the GET the Genotypes section loads a person with, and put on
// the row. The whole sequence runs with the autosave held
// (runWithAutosaveHeld): a save already in flight settles before the push goes
// out, so it cannot land on the server after it, and no save starts until the
// row holds what the server holds.
//
// The proband is addressed the way the endpoints address them: with
// `isProband` both the push and the read go to the family root, and the row
// that carries `proBandId` is the one patched. (The pedigree save never writes
// the proband's genotypes, so there is nothing to lose there, but the row is
// what the chart's labels, legend and letters read.)
//
// The SERVER copies a Test's variants into the person's genotypes too, on its
// own, as part of PUT /clinical-notes/test, and answers with what it stored
// (`genotypeCopy`). The form's own push covers its one person; a bulk Test
// save, which replays the same Test for everyone else selected, has only the
// server's copy. syncMemberGenotypesRowsAfter reads all of those people back
// under one hold (see there).
//
// A read is only put on the row when it holds every genotype the server has
// just said it stored. One that does not is not a read of what was written —
// the wrong person (a proband read without `isProband` answers with an empty
// list, not an error) or a read that overtook the write — and putting it on
// the row would be writing a stale row. The server's own answer goes on
// instead.

/**
 * The person's genotypes as the server now holds them. Null when the answer
 * carries no list, which the caller treats as a failed read.
 */
export const fetchMemberGenotypes = ({ user_id, familyMemberId, isProband }) =>
  axios
    .get(
      APICONSTANTS.getGenotypesById
        .replace(':user_id', user_id)
        .replace(':familyMemberId', familyMemberId),
      { params: { isProband: Boolean(isProband) } }
    )
    .then(({ data }) =>
      Array.isArray(data?.genotypes) ? data.genotypes : null
    );

const replaceRowGenotypes = (records, identity, build) => {
  if (!Array.isArray(records) || identity == null) return records;
  let changed = false;
  const next = records.map((m) => {
    if (memberIdentityOf(m) !== identity) return m;
    const genotypes = build(Array.isArray(m.genotypes) ? m.genotypes : []);
    if (!genotypesDiffer(genotypes, m.genotypes)) return m;
    changed = true;
    return { ...m, genotypes };
  });
  return changed ? next : records;
};

/**
 * Put the server's genotypes on the row. A genotype the row gained after the
 * read was sent (`sentIds` is what it held then) and the server did not return
 * is kept: the read predates it, and dropping it here would let the next save
 * delete it. Returns `records` itself when nothing changed.
 */
export const withMemberGenotypes = (records, identity, genotypes, sentIds) =>
  replaceRowGenotypes(records, identity, (current) => [
    ...genotypes,
    ...notYetIn(
      genotypes,
      current.filter((g) => !sentIds?.has(g?.id))
    )
  ]);

/**
 * The fallback when the read fails after a push succeeded: each push answers
 * with the genotype it stored, so those replace the row's copies by id (or are
 * added). Everything else on the row is left as it was.
 */
export const withPushedGenotypes = (records, identity, pushed) =>
  replaceRowGenotypes(records, identity, (current) => {
    const stored = (pushed || []).filter((g) => g && g.id != null);
    if (!stored.length) return current;
    const byId = new Map(stored.map((g) => [String(g.id), g]));
    const kept = current.map((g) => byId.get(String(g?.id)) || g);
    const keptIds = new Set(kept.map((g) => String(g?.id)));
    return [...kept, ...stored.filter((g) => !keptIds.has(String(g.id)))];
  });

/**
 * Whether a read of the person's genotypes holds every genotype the server
 * said it stored. Anything else is not a read of what was written.
 */
const readHoldsStored = (genotypes, stored) => {
  const ids = new Set((genotypes || []).map((g) => String(g?.id)));
  return (stored || []).every((g) => g?.id == null || ids.has(String(g.id)));
};

/**
 * Read one person's genotypes back onto their row. Runs inside a hold the
 * caller has taken. `stored` is what the server said it stored for them (the
 * push answers, the Test save's `genotypeCopy`): the fallback when the read
 * fails or does not hold it. Nothing happens when this chart has no row for
 * the person.
 */
async function readGenotypesOntoRow({
  rowSync,
  user_id,
  familyMemberId,
  isProband,
  stored
}) {
  const identity = endpointTargetIdentity(rowSync.getMemberRecords(), {
    isProband: Boolean(isProband),
    familyMemberId
  });
  if (identity == null) return;

  const rowNow = rowSync
    .getMemberRecords()
    .find((m) => memberIdentityOf(m) === identity);
  const sentIds = new Set((rowNow?.genotypes || []).map((g) => g?.id));

  let genotypes = null;
  if (user_id) {
    try {
      genotypes = await fetchMemberGenotypes({
        user_id,
        familyMemberId,
        isProband
      });
    } catch {
      genotypes = null;
    }
  }

  if (genotypes && readHoldsStored(genotypes, stored)) {
    rowSync.setMemberRecords((prev) =>
      withMemberGenotypes(prev, identity, genotypes, sentIds)
    );
  } else {
    rowSync.setMemberRecords((prev) =>
      withPushedGenotypes(prev, identity, stored)
    );
  }
}

const storedList = (list) =>
  (Array.isArray(list) ? list : []).filter((g) => g && g.id != null);

/**
 * Push each gene's variants, then — if any push landed, or the Test save
 * reported that the server copied variants itself (`serverStored`), and this
 * chart holds the person's row — read the person's genotypes back onto the
 * row. With no `rowSync` (no chart around the form) the pushes go out exactly
 * as before.
 *
 * Every push is best-effort, as it always was: a refused push does not fail
 * the Test save. A refused push changes nothing on the server, so on its own
 * it leaves the row alone; but the server's copy may already have landed, and
 * then the row is read back all the same.
 *
 * @param {object} args
 * @param {object|null} args.rowSync  useMemberRowSync()
 * @param {string} args.user_id
 * @param {string} args.familyMemberId
 * @param {boolean} args.isProband
 * @param {Array<{gene: string, variants: Array<object>}>} args.pushes
 * @param {Array<object>} [args.serverStored]  the Test save's
 *        `genotypeCopy.genotypes`: what the server copied on its own
 */
export async function pushVariantsOntoMemberRow({
  rowSync,
  user_id,
  familyMemberId,
  isProband,
  pushes,
  serverStored
}) {
  const pushAll = () =>
    Promise.all(
      pushes.map(({ gene, variants }) =>
        pushVariantsToGenotype({
          user_id,
          familyMemberId,
          isProband,
          gene,
          name: gene,
          variants
        }).then(
          (res) => ({ ok: true, genotype: res?.data?.genotype || null }),
          () => ({ ok: false, genotype: null })
        )
      )
    );

  if (!rowSync) {
    await pushAll();
    return;
  }

  await rowSync.runWithAutosaveHeld(
    async () => {
      const results = await pushAll();
      const copied = storedList(serverStored);
      if (!results.some((r) => r.ok) && !copied.length) return;

      await readGenotypesOntoRow({
        rowSync,
        user_id,
        familyMemberId,
        isProband,
        // A push answers after the copy, so it goes last: withPushedGenotypes
        // keeps the later of two copies of a gene.
        stored: [...copied, ...storedList(results.map((r) => r.genotype))]
      });
    },
    { reason: 'test-variant-push' }
  );
}

/**
 * Run `write` — requests after which the SERVER has changed several people's
 * genotypes on its own — and read each of those people's genotypes back onto
 * their row, all under ONE autosave hold.
 *
 * The hold is taken before `write` starts: a save already in flight settles
 * first, so it cannot land on the server after a copy and delete it, and no
 * save starts until every row holds what the server holds. A save that was
 * pending is sent once afterwards, from the patched rows. The reads run side
 * by side; one that fails puts the server's own answer (`stored`) on that
 * person's row and leaves the others alone.
 *
 * The hold's cap is MAX_AUTOSAVE_HOLD_MS per request (`requestCount` writes,
 * plus the reads), not per batch: the cap is for a request that never
 * settles, and a bulk save of ten people is ten requests that each may take
 * seconds (each copy looks every variant up in ClinVar). Capped as one, it
 * would release mid-batch and send the rows already copied, stale.
 *
 * With no `rowSync` (no chart) `write` runs on its own.
 *
 * @param {object} args
 * @param {object|null} args.rowSync  useMemberRowSync()
 * @param {string} args.user_id       the family root (the endpoints' :user_id)
 * @param {() => Promise<Array<{familyMemberId: string, isProband: boolean,
 *         stored: Array<object>}>>} args.write  resolves with the people the
 *         server copied variants for, and what it stored for each
 * @param {number} [args.requestCount]  how many requests `write` makes
 * @param {string} [args.reason]
 */
export async function syncMemberGenotypesRowsAfter({
  rowSync,
  user_id,
  write,
  requestCount = 1,
  reason = 'test-variant-copy'
}) {
  if (!rowSync) {
    await write();
    return;
  }

  await rowSync.runWithAutosaveHeld(
    async () => {
      const people = ((await write()) || []).filter(
        (p) => p && p.familyMemberId && storedList(p.stored).length
      );
      await Promise.all(
        people.map((p) =>
          readGenotypesOntoRow({
            rowSync,
            user_id,
            familyMemberId: p.familyMemberId,
            isProband: p.isProband,
            stored: storedList(p.stored)
          }).catch((err) => {
            console.error('Failed to put copied genotypes on the row:', err);
          })
        )
      );
    },
    {
      reason,
      maxHoldMs: MAX_AUTOSAVE_HOLD_MS * (Math.max(1, requestCount) + 1)
    }
  );
}
